import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { getAgentById, fetchData } from "../../api/client";
import {
  buildRefineDetailLines,
  getRefinementSummaryFromResponse,
  normalizePdfTemplateAnalyzeConfig,
} from "../../utils/pdfTemplateAnalyzeResponse";
import { useAgents } from "../../hooks/useAgents";
import { useWorkspaceSegment } from "../../hooks/useWorkspaceSegment";
import { WorkspaceSegments } from "../../constants/workspaceSegments";
import { useEffectivePermissions, toSet, can } from "../../hooks/useEffectivePermissions";
import { useS3Upload } from "../../hooks/useS3Upload";
import SkillSelector from "../../components/Agents/SkillSelector";
import AgentStatusToggle from "../../components/Agents/AgentStatusToggle";
import AgentDocumentsSection from "../../components/Agents/AgentDocumentsSection";
import AgentSimulator from "../../components/Agents/AgentSimulator";
import AgentFormSection from "../../components/Agents/AgentFormSection";
import AgentFormStepNav from "../../components/Agents/AgentFormStepNav";
import AgentSkillConfigPanel from "../../components/Agents/AgentSkillConfigPanel";
import AgentKnowledgePanel from "../../components/Agents/AgentKnowledgePanel";
import AgentN8nConnectionPanel from "../../components/Agents/AgentN8nConnectionPanel";
import AgentAiSettingsPanel from "../../components/Agents/AgentAiSettingsPanel";
import { buildAgentFormSections, scrollToAgentFormSection } from "../../utils/agentFormSections";
import { translateSkillForm } from "../../utils/skillLifecycle";
import { aggregateKnowledgeQuality } from "../../utils/faqSkillConfig";
import PdfTemplateQualityPanel from "../../components/Agents/PdfTemplateQualityPanel";

// Limites usados pelo RAG do backend (agentRagService / faqSkill)
const MAX_RAG_DOCS = 8; // loadAgentKnowledgeChunks: documents.slice(0, 8)
const MAX_RAG_URLS = 6; // loadAgentKnowledgeChunks: urls.slice(0, 6)

const PDF_TEMPLATE_SKILL_KEYS = new Set(["proposal_generator", "contract_generator"]);
const APPOINTMENT_SCHEDULER_SKILL_KEY = "appointment_scheduler";
const SDR_SKILL_KEY = "sdr";
const PASSAGEM_RECOVERY_SKILL_KEY = "passagem_recovery";

function pdfSkillFieldSecondary(f, t) {
  const fixed = f?.fixed_value != null ? String(f.fixed_value).trim() : "";
  const isFixed = Boolean(fixed) && f?.collect_in_chat === false;
  if (isFixed) {
    return t("agentsListPage.pdfTemplateFieldFixed", { value: fixed });
  }
  if (f?.template_text) {
    return t("agentsListPage.pdfTemplateFieldSample", { sample: f.template_text });
  }
  if (f?.type) {
    return t("agentsListPage.pdfTemplateFieldType", { type: f.type });
  }
  return null;
}

/** Converte status do backend para boolean (toggle). */
const statusToBoolean = (v) => v === "active" || v === true;

/** Converte boolean para status do backend. */
const booleanToStatus = (v) => (v ? "active" : "inactive");

function coerceSkillConfig(raw) {
  if (raw == null || raw === "") return {};
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return p && typeof p === "object" && !Array.isArray(p) ? p : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === "object" && !Array.isArray(raw)) return { ...raw };
  return {};
}

/** Normaliza skills do backend (array de { skill_key, config?, order_index? }) para o form. */
const normalizeSkillsFromBackend = (raw) => {
  if (!raw) return [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((s) => {
      if (typeof s !== "object" || !s?.skill_key) return null;
      return { ...s, config: coerceSkillConfig(s.config) };
    })
    .filter(Boolean)
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
};

/** Mesmo shape que `usePaymentMethods` usa ao listar `/payment-methods`. */
function normalizePaymentMethodsListResponse(response) {
  const payload = response ?? {};
  const inner = payload.data ?? {};
  if (Array.isArray(inner.data)) return inner.data;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

const AgentForm = () => {
  const { t } = useTranslation("settings");
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { segment, isLoading: isLoadingSegment } = useWorkspaceSegment();
  const showTicketSellerSkillOption =
    isLoadingSegment || segment === WorkspaceSegments.PassengerTransport;
  const canConfigureTicketSeller =
    !isLoadingSegment && segment === WorkspaceSegments.PassengerTransport;

  const { data: permData } = useEffectivePermissions();
  const scopeSet = useMemo(() => toSet(permData?.scopes), [permData?.scopes]);
  const canUpdate = can(scopeSet, "agents", "update");
  const canCreate = can(scopeSet, "agents", "create");
  const canViewPaymentMethods = can(scopeSet, "payment_methods", "view");
  const readOnly = isEdit && !canUpdate;

  const [name, setName] = useState("");
  const [engineType, setEngineType] = useState("xpro"); // 'xpro' ou 'n8n'
  const [model, setModel] = useState("gpt-4o-mini");
  const [temperature, setTemperature] = useState(0.7);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [n8nWebhookUrl, setN8nWebhookUrl] = useState("");
  const [n8nHeadersJson, setN8nHeadersJson] = useState("{\n  \"X-API-KEY\": \"seu-token-aqui\"\n}");
  const [status, setStatus] = useState(true);
  const [skills, setSkills] = useState([{ skill_key: "receptionist", config: {}, order_index: 0 }]);
  const [sourceUrls, setSourceUrls] = useState([]);
  const [newUrl, setNewUrl] = useState("");
  const [documents, setDocuments] = useState([]); // { id, file?, url?, name }[]
  const [knowledgePreviews, setKnowledgePreviews] = useState({});
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [submitError, setSubmitError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  const handleKnowledgePreviewUpdate = useCallback((key, preview) => {
    if (!key) return;
    setKnowledgePreviews((prev) => ({ ...prev, [key]: preview }));
  }, []);

  const primarySkillKey = useMemo(() => {
    const s = skills[0];
    return s && typeof s === "object" && s.skill_key ? String(s.skill_key) : null;
  }, [skills]);

  const ticketSellerCfg = useMemo(() => {
    if (primarySkillKey !== "ticket_seller") return {};
    const s = skills[0];
    const c = s?.config;
    return c && typeof c === "object" && !Array.isArray(c) ? c : {};
  }, [skills, primarySkillKey]);

  const productSellerCfg = useMemo(() => {
    if (primarySkillKey !== "product_seller") return {};
    const s = skills[0];
    const c = s?.config;
    return c && typeof c === "object" && !Array.isArray(c) ? c : {};
  }, [skills, primarySkillKey]);

  const faqCfg = useMemo(() => {
    if (primarySkillKey !== "faq") return {};
    const s = skills[0];
    const c = s?.config;
    return c && typeof c === "object" && !Array.isArray(c) ? c : {};
  }, [skills, primarySkillKey]);

  const proposalCfg = useMemo(() => {
    if (primarySkillKey !== "proposal_generator") return {};
    const s = skills[0];
    const c = s?.config;
    return c && typeof c === "object" && !Array.isArray(c) ? c : {};
  }, [skills, primarySkillKey]);

  const contractCfg = useMemo(() => {
    if (primarySkillKey !== "contract_generator") return {};
    const s = skills[0];
    const c = s?.config;
    return c && typeof c === "object" && !Array.isArray(c) ? c : {};
  }, [skills, primarySkillKey]);

  const [analyzingProposal, setAnalyzingProposal] = useState(false);
  const [proposalFieldFeedback, setProposalFieldFeedback] = useState("");
  const [refiningProposalFields, setRefiningProposalFields] = useState(false);
  const [analyzingContract, setAnalyzingContract] = useState(false);
  const [contractFieldFeedback, setContractFieldFeedback] = useState("");
  const [refiningContractFields, setRefiningContractFields] = useState(false);
  const [proposalRefineResult, setProposalRefineResult] = useState(null);
  const [contractRefineResult, setContractRefineResult] = useState(null);

  const pdfDocuments = useMemo(
    () =>
      (documents || []).filter((d) => {
        const name = String(d?.name || d?.file_name || "").toLowerCase();
        return name.endsWith(".pdf");
      }),
    [documents]
  );

  const ticketSellerEnabled =
    engineType === "xpro" && primarySkillKey === "ticket_seller" && canConfigureTicketSeller;

  const productSellerEnabled = engineType === "xpro" && primarySkillKey === "product_seller";

  const paymentMethodsSkillEnabled = ticketSellerEnabled || productSellerEnabled;

  const proposalEnabled = engineType === "xpro" && primarySkillKey === "proposal_generator";
  const contractEnabled = engineType === "xpro" && primarySkillKey === "contract_generator";
  const pdfTemplateSkillEnabled = proposalEnabled || contractEnabled;
  const isPdfTemplateSkill = Boolean(primarySkillKey && PDF_TEMPLATE_SKILL_KEYS.has(primarySkillKey));

  const handleAnalyzeProposalTemplate = async () => {
    if (!isEdit || !id) {
      setSnack({ open: true, severity: "info", message: t("agentsListPage.skillParamsProposalAnalyzeNeedSave") });
      return;
    }
    setAnalyzingProposal(true);
    try {
      const res = await fetchData(`/agents/${id}/proposal-template/analyze`, "POST", {
        template_document_id: proposalCfg.template_document_id || pdfDocuments[0]?.id || null,
        config: proposalCfg,
      });
      const cfg = res?.data?.config || res?.config;
      if (cfg && typeof cfg === "object") {
        mergeSkillConfig("proposal_generator", cfg);
        setSnack({
          open: true,
          severity: "success",
          message: `Modelo analisado: ${(cfg.fields_schema || []).length} campo(s) detectado(s)${
            cfg.analyze_mode === "ai" ? " (análise inteligente)" : ""
          }.`,
        });
      } else {
        setSnack({ open: true, severity: "success", message: "Modelo analisado com sucesso." });
      }
    } catch (err) {
      const msg = err?.message || "Erro ao analisar modelo PDF.";
      setSnack({ open: true, severity: "error", message: msg });
    } finally {
      setAnalyzingProposal(false);
    }
  };

  const handleRefineProposalFields = async () => {
    if (!isEdit || !id) {
      setSnack({ open: true, severity: "info", message: t("agentsListPage.skillParamsProposalAnalyzeNeedSave") });
      return;
    }
    const feedback = proposalFieldFeedback.trim();
    if (feedback.length < 10) {
      setSnack({ open: true, severity: "warning", message: t("agentsListPage.pdfTemplateAdjustFeedbackTooShort") });
      return;
    }
    setRefiningProposalFields(true);
    setProposalRefineResult(null);
    try {
      const prevFields = proposalCfg.fields_schema || [];
      const res = await fetchData(`/agents/${id}/proposal-template/analyze`, "POST", {
        template_document_id: proposalCfg.template_document_id || pdfDocuments[0]?.id || null,
        config: proposalCfg,
        user_feedback: feedback,
      });
      const cfg = normalizePdfTemplateAnalyzeConfig(res);
      if (!cfg) {
        setSnack({ open: true, severity: "error", message: t("agentsListPage.pdfTemplateRefineResponseError") });
        return;
      }
      const summary = getRefinementSummaryFromResponse(res, prevFields);
      mergeSkillConfig("proposal_generator", cfg);
      setProposalFieldFeedback("");
      setProposalRefineResult({ summary, detailLines: buildRefineDetailLines(summary, t) });
      setSnack({ open: true, severity: "success", message: t("agentsListPage.pdfTemplateRefineSuccess") });
    } catch (err) {
      const msg = err?.message || t("agentsListPage.pdfTemplateRefineError");
      setSnack({ open: true, severity: "error", message: msg });
    } finally {
      setRefiningProposalFields(false);
    }
  };

  const handleAnalyzeContractTemplate = async () => {
    if (!isEdit || !id) {
      setSnack({ open: true, severity: "info", message: t("agentsListPage.skillParamsContractAnalyzeNeedSave") });
      return;
    }
    setAnalyzingContract(true);
    try {
      const res = await fetchData(`/agents/${id}/contract-template/analyze`, "POST", {
        template_document_id: contractCfg.template_document_id || pdfDocuments[0]?.id || null,
        config: contractCfg,
      });
      const cfg = res?.data?.config || res?.config;
      if (cfg && typeof cfg === "object") {
        mergeSkillConfig("contract_generator", cfg);
        setSnack({
          open: true,
          severity: "success",
          message: `Modelo analisado: ${(cfg.fields_schema || []).length} campo(s) detectado(s)${
            cfg.analyze_mode === "ai" ? " (análise inteligente)" : ""
          }.`,
        });
      } else {
        setSnack({ open: true, severity: "success", message: "Modelo analisado com sucesso." });
      }
    } catch (err) {
      const msg = err?.message || "Erro ao analisar modelo PDF.";
      setSnack({ open: true, severity: "error", message: msg });
    } finally {
      setAnalyzingContract(false);
    }
  };

  const handleRefineContractFields = async () => {
    if (!isEdit || !id) {
      setSnack({ open: true, severity: "info", message: t("agentsListPage.skillParamsContractAnalyzeNeedSave") });
      return;
    }
    const feedback = contractFieldFeedback.trim();
    if (feedback.length < 10) {
      setSnack({ open: true, severity: "warning", message: t("agentsListPage.pdfTemplateAdjustFeedbackTooShort") });
      return;
    }
    setRefiningContractFields(true);
    setContractRefineResult(null);
    try {
      const prevFields = contractCfg.fields_schema || [];
      const res = await fetchData(`/agents/${id}/contract-template/analyze`, "POST", {
        template_document_id: contractCfg.template_document_id || pdfDocuments[0]?.id || null,
        config: contractCfg,
        user_feedback: feedback,
      });
      const cfg = normalizePdfTemplateAnalyzeConfig(res);
      if (!cfg) {
        setSnack({ open: true, severity: "error", message: t("agentsListPage.pdfTemplateRefineResponseError") });
        return;
      }
      const summary = getRefinementSummaryFromResponse(res, prevFields);
      mergeSkillConfig("contract_generator", cfg);
      setContractFieldFeedback("");
      setContractRefineResult({ summary, detailLines: buildRefineDetailLines(summary, t) });
      setSnack({ open: true, severity: "success", message: t("agentsListPage.pdfTemplateRefineSuccess") });
    } catch (err) {
      const msg = err?.message || t("agentsListPage.pdfTemplateRefineError");
      setSnack({ open: true, severity: "error", message: msg });
    } finally {
      setRefiningContractFields(false);
    }
  };

  const { data: paymentMethodsList = [], isLoading: paymentMethodsLoading, isError: paymentMethodsError } = useQuery(
    ["agentFormPaymentMethods", canViewPaymentMethods],
    () => fetchData(`/payment-methods?page=1&limit=500&search=`),
    {
      enabled: paymentMethodsSkillEnabled && canViewPaymentMethods,
      staleTime: 60_000,
      select: normalizePaymentMethodsListResponse,
    }
  );

  const paymentMethodsForSelect = useMemo(() => {
    const rows = Array.isArray(paymentMethodsList) ? paymentMethodsList : [];
    const active = rows.filter((m) => m && Number(m.status) === 1);
    active.sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), undefined, { sensitivity: "base" }));
    return active;
  }, [paymentMethodsList]);

  const selectedFormaIdStr = useMemo(() => {
    if (primarySkillKey === "product_seller") {
      const v = productSellerCfg.default_payment_method_id ?? productSellerCfg.id_formadepagamento;
      if (v == null || v === "") return "";
      return String(v).trim();
    }
    const v = ticketSellerCfg.id_formadepagamento;
    if (v == null || v === "") return "";
    return String(v).trim();
  }, [primarySkillKey, ticketSellerCfg.id_formadepagamento, productSellerCfg.default_payment_method_id, productSellerCfg.id_formadepagamento]);

  const orphanFormaRow = useMemo(() => {
    if (!selectedFormaIdStr) return null;
    const rows = Array.isArray(paymentMethodsList) ? paymentMethodsList : [];
    return rows.find((m) => m && String(m.id) === selectedFormaIdStr) || null;
  }, [paymentMethodsList, selectedFormaIdStr]);

  const mergeSkillConfig = React.useCallback((skillKey, partial) => {
    setSkills((prev) => {
      const row = prev[0];
      if (!row || row.skill_key !== skillKey) return prev;
      const base = row.config && typeof row.config === "object" && !Array.isArray(row.config) ? { ...row.config } : {};
      const resolved = typeof partial === "function" ? partial(base) : partial;
      if (!resolved || typeof resolved !== "object") return prev;
      return [{ ...row, config: { ...base, ...resolved } }];
    });
  }, []);

  const fileInputRef = React.useRef(null);
  const documentIdRef = React.useRef(0);
  const multiSkillMigratedRef = React.useRef(false);

  // O backend usa `mammoth` para DOCX. O formato `.doc` legacy tende a falhar/retornar texto vazio.
  // Por isso, evitamos o upload de `.doc` no frontend.
  const ACCEPT_DOCS = ".pdf,.docx,.xls,.xlsx";
  const { uploadMany: uploadManyDocs } = useS3Upload();

  const buildDocumentsPrefix = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `agents/documents/${yyyy}/${mm}`;
  };

  const handleAddUrl = () => {
    const url = newUrl.trim();
    if (!url) return;
    try {
      new URL(url);
    } catch {
      setSnack({ open: true, severity: "warning", message: "Informe uma URL válida." });
      return;
    }
    setSourceUrls((prev) => {
      if (prev.includes(url)) return prev;
      if (prev.length >= MAX_RAG_URLS) {
        setSnack({
          open: true,
          severity: "warning",
          message: `Limite de ${MAX_RAG_URLS} links de sites por agente. Remova links antes de adicionar mais.`,
        });
        return prev;
      }
      return [...prev, url];
    });
    setNewUrl("");
  };

  const handleRemoveUrl = (index) => {
    setSourceUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const validateDocFiles = (fileList) => {
    const isPdfOnlySkill = pdfTemplateSkillEnabled;
    const exts = isPdfOnlySkill ? [".pdf"] : [".pdf", ".docx", ".xls", ".xlsx"];
    const accepted = [];
    const errors = [];
    const MAX_MB = 15;
    Array.from(fileList || []).forEach((f) => {
      const name = (f && f.name) || "";
      const ext = name.toLowerCase().slice(name.lastIndexOf("."));
      if (ext === ".doc") {
        errors.push(`${name || "arquivo"}: formato .doc legacy não suportado. Use .docx.`);
        return;
      }
      const sizeOk = f && typeof f.size === "number" && f.size <= MAX_MB * 1024 * 1024;
      if (!exts.includes(ext)) {
        errors.push(`${name || "arquivo"}: tipo inválido (${isPdfOnlySkill ? "apenas PDF" : "PDF, Word (.docx) e planilhas"}).`);
      } else if (!sizeOk) {
        errors.push(`${name || "arquivo"}: maior que ${MAX_MB}MB.`);
      } else {
        accepted.push(f);
      }
    });
    if (errors.length) {
      setSnack({ open: true, severity: "warning", message: errors.slice(0, 2).join(" ") });
    }
    return accepted;
  };

  const addDocFiles = (fileList) => {
    const files = validateDocFiles(fileList);
    if (!files.length) return;

    const availableSlots = Math.max(0, MAX_RAG_DOCS - documents.length);
    if (availableSlots <= 0) {
      setSnack({
        open: true,
        severity: "warning",
        message: `Limite de ${MAX_RAG_DOCS} documentos por agente. Remova documentos antes de adicionar mais.`,
      });
      return;
    }

    const trimmed = files.slice(0, availableSlots);
    if (trimmed.length !== files.length) {
      setSnack({
        open: true,
        severity: "warning",
        message: `Foram ignorados ${files.length - trimmed.length} documentos por exceder o limite (${MAX_RAG_DOCS}).`,
      });
    }
    setDocuments((prev) => [
      ...prev,
      ...trimmed.map((file) => ({
        id: `tmp_${++documentIdRef.current}`,
        file,
        name: file.name,
      })),
    ]);
  };

  const handleClickAddDocs = () => fileInputRef.current?.click();

  const handleDropDocs = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addDocFiles(e.dataTransfer.files);
  };

  const handleDragOverDocs = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const moveDocument = (idx, dir) => {
    setDocuments((prev) => {
      const list = [...prev];
      const to = idx + dir;
      if (to < 0 || to >= list.length) return prev;
      const [it] = list.splice(idx, 1);
      list.splice(to, 0, it);
      return list;
    });
  };

  const removeDocument = (idx) => {
    setDocuments((prev) => prev.filter((_, i) => i !== idx));
  };

  const { data: agentData, isLoading: loadingAgent } = useQuery(
    ["agent", id],
    () => getAgentById(id),
    {
      enabled: isEdit,
      // Evita refetch (foco da janela) sobrescrever edições locais — ex.: vídeos da ops
      // intelligence “desmarcando sozinhos”.
      refetchOnWindowFocus: false,
    }
  );

  const agent = agentData?.data ?? agentData;
  const lastHydratedAgentKeyRef = React.useRef(null);

  useEffect(() => {
    if (!agent || !isEdit) return;
    const skillsKey = JSON.stringify(
      (Array.isArray(agent.skills) ? agent.skills : []).map((s) => ({
        skill_key: s.skill_key,
        order_index: s.order_index,
        config: s.config ?? s.config_json ?? {},
      }))
    );
    const hydrateKey = [
      String(agent.id || id || ""),
      String(agent.updated_at || agent.data_ultima_alteracao || ""),
      String(agent.status ?? ""),
      String(agent.model ?? agent.modelo ?? ""),
      skillsKey,
    ].join("|");
    if (lastHydratedAgentKeyRef.current === hydrateKey) return;
    lastHydratedAgentKeyRef.current = hydrateKey;

    setName(agent.name ?? agent.nome ?? "");
    const agentModel = agent.model ?? agent.modelo ?? "gpt-4o-mini";
    setModel(agentModel);
    setTemperature(Number(agent.temperature) ?? 0.7);
    const rawSystemPrompt = agent.system_prompt ?? agent.prompt ?? agent.prompt_base ?? "";
    // Detecta agente N8N pelo model
    if (String(agentModel).toLowerCase() === "n8n") {
      setEngineType("n8n");
      // Para N8N, esperamos JSON em system_prompt com config do webhook
      try {
        const parsed = typeof rawSystemPrompt === "string" ? JSON.parse(rawSystemPrompt || "{}") : rawSystemPrompt || {};
        if (parsed && typeof parsed === "object") {
          if (parsed.webhook_url) setN8nWebhookUrl(parsed.webhook_url);
          if (parsed.headers) {
            try {
              setN8nHeadersJson(JSON.stringify(parsed.headers, null, 2));
            } catch {
              setN8nHeadersJson("{\n  \"X-API-KEY\": \"seu-token-aqui\"\n}");
            }
          }
        }
      } catch {
        // Se não for JSON válido, mantém campos padrão
      }
      // Para agentes N8N o system_prompt é usado como config JSON, então não exibimos no campo de texto
      setSystemPrompt("");
    } else {
      setEngineType("xpro");
      setSystemPrompt(rawSystemPrompt);
    }
    setStatus(statusToBoolean(agent.status ?? agent.enabled ?? agent.is_active));
    const rawSkills = normalizeSkillsFromBackend(agent.skills);
    const sortedSkills = [...rawSkills].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
    if (sortedSkills.length > 1) {
      setSkills([sortedSkills[0]]);
      if (!multiSkillMigratedRef.current) {
        multiSkillMigratedRef.current = true;
        setSnack({ open: true, severity: "info", message: t("agentsListPage.skillMultiMigratedHint") });
      }
    } else if (sortedSkills.length === 1) {
      setSkills(sortedSkills);
    } else {
      setSkills([{ skill_key: "receptionist", config: {}, order_index: 0 }]);
    }
    const urls = agent.source_urls ?? agent.urls ?? agent.reference_urls;
    setSourceUrls(Array.isArray(urls) ? urls.filter(Boolean) : []);
    const docs = agent.documents ?? agent.documentos ?? [];
    setDocuments(
      Array.isArray(docs)
        ? docs.map((d) => ({
            id: d.id,
            url: d.file_url ?? d.url,
            name: d.file_name ?? d.name ?? "documento",
          }))
        : []
    );
  }, [agent, isEdit, t, id]);

  useEffect(() => {
    multiSkillMigratedRef.current = false;
    lastHydratedAgentKeyRef.current = null;
    setKnowledgePreviews({});
  }, [id]);

  useEffect(() => {
    if (isLoadingSegment) return;
    if (segment === WorkspaceSegments.PassengerTransport) return;
    if (primarySkillKey !== "ticket_seller") return;
    setSkills([{ skill_key: "receptionist", config: {}, order_index: 0 }]);
  }, [isLoadingSegment, segment, primarySkillKey]);

  useEffect(() => {
    if (engineType !== "xpro") return;
    setSkills((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) {
        return [{ skill_key: "receptionist", config: {}, order_index: 0 }];
      }
      if (prev.length > 1) {
        const sorted = [...prev].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
        return [sorted[0]];
      }
      return prev;
    });
  }, [engineType]);

  useEffect(() => {
    const onAddFiles = (ev) => {
      const files = ev.detail || [];
      addDocFiles(files);
    };
    window.addEventListener("agent-documents:addFiles", onAddFiles);
    return () => window.removeEventListener("agent-documents:addFiles", onAddFiles);
  }, []);

  const { createMutation, updateMutation } = useAgents();
  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    // Validações de consistência com o RAG do backend.
    // Isso evita criar/editar um agente "configurado", mas que não consegue extrair conhecimento.
    if (engineType === "xpro") {
      if (!Array.isArray(skills) || skills.length !== 1 || !skills[0]?.skill_key) {
        const msg = t("agentsListPage.skillExactlyOneError");
        setSubmitError(msg);
        setSnack({ open: true, severity: "warning", message: msg });
        return;
      }

      if (
        (skills[0].skill_key === "ticket_seller" || skills[0].skill_key === PASSAGEM_RECOVERY_SKILL_KEY) &&
        !isLoadingSegment &&
        segment !== WorkspaceSegments.PassengerTransport
      ) {
        const msg =
          skills[0].skill_key === PASSAGEM_RECOVERY_SKILL_KEY
            ? "A recuperação de vendas de passagens só está disponível em workspaces do segmento transporte rodoviário de passageiros."
            : "A capacidade de venda de passagens (BPe) só está disponível em workspaces do segmento transporte rodoviário de passageiros.";
        setSubmitError(msg);
        setSnack({ open: true, severity: "warning", message: msg });
        return;
      }

      if (skills[0].skill_key === APPOINTMENT_SCHEDULER_SKILL_KEY) {
        const cfg = coerceSkillConfig(skills[0].config);
        if (!cfg.calendar_id) {
          const msg = t("agentsListPage.skillAppointmentCalendarRequired");
          setSubmitError(msg);
          setSnack({ open: true, severity: "warning", message: msg });
          return;
        }
      }

      const usesKnowledgeRag =
        skills[0].skill_key === "faq" ||
        skills[0].skill_key === "ticket_seller" ||
        skills[0].skill_key === "social_media" ||
        skills[0].skill_key === SDR_SKILL_KEY;
      if (usesKnowledgeRag) {
        if (documents.length > MAX_RAG_DOCS) {
          const skillLabel =
            skills[0].skill_key === "faq"
              ? "FAQ"
              : skills[0].skill_key === SDR_SKILL_KEY
                ? "SDR"
                : skills[0].skill_key === "social_media"
                  ? "Social Media"
                  : "venda de passagens";
          const msg = `Você excedeu o limite de ${MAX_RAG_DOCS} documentos. O agente de ${skillLabel} só considera os primeiros ${MAX_RAG_DOCS}. Remova os excedentes.`;
          setSubmitError(msg);
          setSnack({ open: true, severity: "warning", message: msg });
          return;
        }

        if (sourceUrls.length > MAX_RAG_URLS) {
          const skillLabel =
            skills[0].skill_key === "faq"
              ? "FAQ"
              : skills[0].skill_key === SDR_SKILL_KEY
                ? "SDR"
                : skills[0].skill_key === "social_media"
                  ? "Social Media"
                  : "venda de passagens";
          const msg = `Você excedeu o limite de ${MAX_RAG_URLS} links de sites. O agente de ${skillLabel} só considera os primeiros ${MAX_RAG_URLS}. Remova os excedentes.`;
          setSubmitError(msg);
          setSnack({ open: true, severity: "warning", message: msg });
          return;
        }

        const hasLegacyDoc = documents.some((d) => String(d?.name || "").toLowerCase().endsWith(".doc"));
        if (hasLegacyDoc) {
          const msg =
            "Existem documentos no formato .doc legacy. O RAG do FAQ usa mammoth e recomenda .docx. Remova/substitua por .docx.";
          setSubmitError(msg);
          setSnack({ open: true, severity: "warning", message: msg });
          return;
        }

        // Soft gate: bloqueia só ao ativar FAQ sem fonte utilizável.
        // Agente já ativo com PDF ruim: alerta, não força inactive.
        if (skills[0].skill_key === "faq" && status) {
          const quality = aggregateKnowledgeQuality(knowledgePreviews, { documents, sourceUrls });
          const wasActive = isEdit && statusToBoolean(agent?.status ?? agent?.enabled ?? agent?.is_active);
          const noSources = documents.length === 0 && sourceUrls.length === 0;
          if (!wasActive && noSources) {
            const msg = t("agentsListPage.faqKnowledgeRequiredToActivate");
            setSubmitError(msg);
            setSnack({ open: true, severity: "warning", message: msg });
            return;
          }
          if (noSources) {
            setSnack({
              open: true,
              severity: "warning",
              message: t("agentsListPage.faqKnowledgeRequiredToActivate"),
            });
          }
          if (!wasActive && quality === "empty") {
            const msg = t("agentsListPage.faqKnowledgeRequiredToActivate");
            setSubmitError(msg);
            setSnack({ open: true, severity: "warning", message: msg });
            return;
          }
          if (quality === "empty" || quality === "low") {
            setSnack({
              open: true,
              severity: "warning",
              message: t("agentsListPage.faqKnowledgeQualityWarning"),
            });
          }
        }
      }
    }

    if (engineType === "n8n") {
      const url = String(n8nWebhookUrl || "").trim();
      if (!url) {
        const msg = "Informe a URL do webhook N8N.";
        setSubmitError(msg);
        setSnack({ open: true, severity: "warning", message: msg });
        return;
      }
      try {
        // valida formato básico da URL
        new URL(url);
      } catch {
        const msg = "URL do webhook N8N inválida.";
        setSubmitError(msg);
        setSnack({ open: true, severity: "warning", message: msg });
        return;
      }
      if (n8nHeadersJson) {
        try {
          JSON.parse(n8nHeadersJson);
        } catch {
          const msg = "Headers JSON inválidos. Corrija o JSON ou deixe em branco.";
          setSubmitError(msg);
          setSnack({ open: true, severity: "warning", message: msg });
          return;
        }
      }
    }

    const buildPayload = (docUrls) => {
      // Monta system_prompt / modelo conforme o tipo do agente
      let finalModel = model;
      let finalSystemPrompt = systemPrompt.trim() || null;

      if (engineType === "n8n") {
        finalModel = "n8n";
        let headersObj = {};
        try {
          headersObj = n8nHeadersJson ? JSON.parse(n8nHeadersJson) : {};
        } catch {
          headersObj = {};
        }
        finalSystemPrompt = JSON.stringify(
          {
            webhook_url: n8nWebhookUrl?.trim() || "",
            headers: headersObj,
            timeout_ms: 15000,
          },
          null,
          2
        );
      }

      return {
        name: name.trim() || (agent?.nome ?? agent?.name),
        status: booleanToStatus(status),
        model: finalModel,
        temperature,
        system_prompt: finalSystemPrompt,
        skills: Array.isArray(skills) && skills[0]?.skill_key ? [skills[0]] : [],
        // Sempre enviar arrays para skills com RAG — `undefined` no update não limpa docs/URLs no backend.
        source_urls: sourceUrls.filter((u) => u && String(u).trim()),
        documents: docUrls,
      };
    };

    const novos = documents.filter((d) => d.file);
    let uploadResults = [];

    if (novos.length) {
      try {
        setUploadProgress({ done: 0, total: novos.length });
        uploadResults = await uploadManyDocs(
          novos.map((n) => n.file),
          { prefix: buildDocumentsPrefix() },
          (done, total) => setUploadProgress({ done, total })
        );
        uploadResults.forEach((r, i) => {
          if (!r?.ok || !r?.url) throw new Error(r?.error || `Falha no upload de "${novos[i]?.name}"`);
        });
      } catch (err) {
        setSubmitError(err?.message || "Falha ao enviar documentos.");
        setSnack({ open: true, severity: "error", message: err?.message || "Falha ao enviar documentos." });
        return;
      } finally {
        setUploadProgress({ done: 0, total: 0 });
      }
    }

    const docUrls = documents.map((d) => {
      if (d.url) return { url: d.url, file_name: d.name };
      const i = novos.indexOf(d);
      if (i === -1) return null;
      const r = uploadResults[i];
      return r?.url ? { url: r.url, file_name: d.name } : null;
    }).filter(Boolean);

    const payload = buildPayload(docUrls);

    if (isEdit) {
      updateMutation.mutate(
        { id, data: payload },
        {
          onSuccess: () => {
            setSnack({ open: true, severity: "success", message: "Agente de IA atualizado." });
            navigate(`/agents/${id}`);
          },
          onError: (err) => {
            const msg = err?.message || "Erro ao atualizar o agente de IA.";
            setSubmitError(msg);
            setSnack({ open: true, severity: "error", message: msg });
          },
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (res) => {
          const created = res?.data ?? res;
          const newId = created?.id ?? created?.id_agent;
          setSnack({ open: true, severity: "success", message: "Agente de IA criado." });
          if (newId) navigate(`/agents/${newId}`);
          else navigate("/agents");
        },
        onError: (err) => {
          const msg = err?.message || "Erro ao criar o agente de IA.";
          setSubmitError(msg);
          setSnack({ open: true, severity: "error", message: msg });
        },
      });
    }
  };

  const formSections = useMemo(
    () =>
      buildAgentFormSections({
        engineType,
        primarySkillKey,
        canConfigureTicketSeller,
      }),
    [engineType, primarySkillKey, canConfigureTicketSeller]
  );

  const sectionMeta = useMemo(() => {
    const map = {};
    formSections.forEach((s) => {
      map[s.id] = s;
    });
    return map;
  }, [formSections]);

  const meta = (id) => sectionMeta[id] || { labelKey: "", hintKey: "" };

  const skillConfigSectionHint = primarySkillKey
    ? translateSkillForm(t, primarySkillKey, "formSectionSkillConfigHint", "agentsListPage.formSectionSkillConfigHint")
    : t("agentsListPage.formSectionSkillConfigHint");

  const skillKnowledgeSectionHint = primarySkillKey
    ? translateSkillForm(t, primarySkillKey, "formSectionKnowledgeHint", "agentsListPage.formSectionKnowledgeHint")
    : t("agentsListPage.formSectionKnowledgeHint");

  const skillAiSettingsHint = primarySkillKey
    ? translateSkillForm(t, primarySkillKey, "formSectionAiSettingsHint", "agentsListPage.formSectionAiSettingsHint")
    : t("agentsListPage.formSectionAiSettingsHint");

  if (isEdit && loadingAgent) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">Carregando agente de IA...</Typography>
      </Box>
    );
  }

  return (
    <Box className="tour-agent-form" sx={{ paddingTop: 1, paddingBottom: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 3 },
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          <Button variant="outlined" size="small" startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/agents")}>
            Voltar
          </Button>
          {isEdit && (
            <Button
              variant="contained"
              size="small"
              startIcon={<BarChartRoundedIcon />}
              onClick={() => navigate(`/agents/${id}/analytics`)}
            >
              Estatísticas
            </Button>
          )}
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.25 }}>
          {isEdit ? t("agentsListPage.formEditTitle") : t("agentsListPage.formCreateTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          {isEdit ? t("agentsListPage.formEditSubtitle") : t("agentsListPage.formCreateSubtitle")}
        </Typography>

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <AgentFormStepNav
            sections={formSections}
            onSelectSection={scrollToAgentFormSection}
          />
          <Stack spacing={0} sx={{ pb: { xs: 14, md: 12 } }}>
              <AgentFormSection
                id="identity"
                title={t(meta("identity").labelKey)}
                hint={t(meta("identity").hintKey)}
                fullWidth
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                    gap: 2,
                    alignItems: "stretch",
                    width: "100%",
                  }}
                >
                  <TextField
                    label={t("agentsListPage.formAgentNameLabel")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    fullWidth
                    size="small"
                    placeholder={t("agentsListPage.formAgentNamePlaceholder")}
                    helperText={t("agentsListPage.formAgentNameHelper")}
                    inputProps={{ readOnly }}
                  />
                  <Box
                    sx={{
                      px: 2,
                      py: 1.25,
                      borderRadius: 1,
                      border: 1,
                      borderColor: "divider",
                      bgcolor: "action.hover",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      minHeight: 56,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                        {t("agentsListPage.formAgentStatusLabel")}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.25, lineHeight: 1.3, whiteSpace: { md: "nowrap" } }}
                      >
                        {t("agentsListPage.formAgentStatusHelper")}
                      </Typography>
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                      <AgentStatusToggle value={status} onChange={setStatus} disabled={isEdit && !canUpdate} />
                    </Box>
                  </Box>
                </Box>
              </AgentFormSection>

              <AgentFormSection
                id="function"
                title={t(meta("function").labelKey)}
                hint={t(meta("function").hintKey)}
                fullWidth
              >
                <Stack spacing={2.25} sx={{ width: "100%" }}>
                  <FormControl size="small" disabled={readOnly} sx={{ width: { xs: "100%", sm: 480 } }}>
                    <InputLabel>{t("agentsListPage.formEngineTypeLabel")}</InputLabel>
                    <Select
                      label={t("agentsListPage.formEngineTypeLabel")}
                      value={engineType}
                      onChange={(e) => setEngineType(e.target.value)}
                    >
                      <MenuItem value="xpro">{t("agentsListPage.formEngineXpro")}</MenuItem>
                      <MenuItem value="n8n">{t("agentsListPage.formEngineN8n")}</MenuItem>
                    </Select>
                  </FormControl>
                  {engineType === "xpro" && (
                    <SkillSelector
                      value={skills}
                      onChange={setSkills}
                      disabled={readOnly}
                      showTicketSellerSkill={showTicketSellerSkillOption}
                      includeRoadPassengerCopy={canConfigureTicketSeller}
                    />
                  )}
                  {engineType === "xpro" && !primarySkillKey && (
                    <Alert severity="info" sx={{ py: 0.75, maxWidth: 560 }}>
                      {t("agentsListPage.formSelectSkillPrompt")}
                    </Alert>
                  )}
                </Stack>
              </AgentFormSection>

              {engineType === "n8n" && (
                <AgentFormSection
                  id="n8n"
                  title={t(meta("n8n").labelKey)}
                  hint={t(meta("n8n").hintKey)}
                >
                  <AgentN8nConnectionPanel
                    n8nWebhookUrl={n8nWebhookUrl}
                    setN8nWebhookUrl={setN8nWebhookUrl}
                    n8nHeadersJson={n8nHeadersJson}
                    setN8nHeadersJson={setN8nHeadersJson}
                    readOnly={readOnly}
                  />
                </AgentFormSection>
              )}

              {engineType === "xpro" && primarySkillKey && (
                <AgentFormSection
                  id="skill-config"
                  title={t(meta("skill-config").labelKey)}
                  hint={skillConfigSectionHint}
                  fullWidth
                >
                  <AgentSkillConfigPanel
                    primarySkillKey={primarySkillKey}
                    isPdfTemplateSkill={isPdfTemplateSkill}
                    canConfigureTicketSeller={canConfigureTicketSeller}
                    readOnly={readOnly}
                    mergeSkillConfig={mergeSkillConfig}
                    faqCfg={faqCfg}
                    ticketSellerCfg={ticketSellerCfg}
                    proposalCfg={proposalCfg}
                    contractCfg={contractCfg}
                    skills={skills}
                    currentAgentId={id}
                    canViewPaymentMethods={canViewPaymentMethods}
                    paymentMethodsLoading={paymentMethodsLoading}
                    paymentMethodsError={paymentMethodsError}
                    paymentMethodsForSelect={paymentMethodsForSelect}
                    selectedFormaIdStr={selectedFormaIdStr}
                    orphanFormaRow={orphanFormaRow}
                    knowledgeDocuments={documents}
                    knowledgeSourceUrls={sourceUrls}
                  />
                </AgentFormSection>
              )}

          {/* Modelo de proposta (skill proposal_generator) */}
          {engineType === "xpro" && primarySkillKey === "proposal_generator" && (
            <AgentFormSection
              id="proposal-pdf"
              title={t(meta("proposal-pdf").labelKey)}
              hint={t(meta("proposal-pdf").hintKey)}
              fullWidth
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                {t("agentsListPage.knowledgeSectionProposalIntro")}
              </Typography>
              <Alert severity="info" sx={{ mb: 2.5, py: 0.75 }}>
                {t("agentsListPage.proposalFormatNote")}
              </Alert>

              <Stack spacing={3} divider={<Divider flexItem />}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t("agentsListPage.proposalStep1Title")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t("agentsListPage.proposalStep1Body")}
                  </Typography>
                  <AgentDocumentsSection
                    documents={documents}
                    fileInputRef={fileInputRef}
                    handleClickAdd={handleClickAddDocs}
                    handleDrop={handleDropDocs}
                    handleDragOver={handleDragOverDocs}
                    moveDocument={moveDocument}
                    removeDocument={removeDocument}
                    readOnly={readOnly}
                    accept=".pdf,application/pdf"
                    embedded
                    embeddedDocTypeLabel={t("agentsListPage.proposalUploadLabel")}
                    uploadTip={t("agentsListPage.proposalUploadTip")}
                  />
                  <Alert severity="info" sx={{ mt: 1.5, py: 0.75 }}>
                    {t("agentsListPage.proposalPlaceholderTip")}
                  </Alert>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t("agentsListPage.proposalStep2Title")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t("agentsListPage.proposalStep2Body")}
                  </Typography>
                  {pdfDocuments.length === 0 ? (
                    <Alert severity="info" sx={{ py: 0.75 }}>
                      {t("agentsListPage.proposalStep2NeedPdf")}
                    </Alert>
                  ) : (
                    <Stack spacing={1.5}>
                      {pdfDocuments.length > 1 && (
                        <FormControl fullWidth size="small" disabled={readOnly}>
                          <InputLabel id="agent-proposal-template-label">
                            {t("agentsListPage.skillParamsProposalTemplateDoc")}
                          </InputLabel>
                          <Select
                            labelId="agent-proposal-template-label"
                            label={t("agentsListPage.skillParamsProposalTemplateDoc")}
                            value={proposalCfg.template_document_id || pdfDocuments[0]?.id || ""}
                            onChange={(e) =>
                              mergeSkillConfig("proposal_generator", {
                                template_document_id: e.target.value || "",
                              })
                            }
                          >
                            {pdfDocuments.map((d) => (
                              <MenuItem key={d.id || d.name} value={d.id || d.name}>
                                {d.name || d.file_name || "modelo.pdf"}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                      {!isEdit && (
                        <Alert severity="info" sx={{ py: 0.75 }}>
                          {t("agentsListPage.skillParamsProposalAnalyzeNeedSave")}
                        </Alert>
                      )}
                      <Box>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleAnalyzeProposalTemplate}
                          disabled={readOnly || analyzingProposal || !isEdit}
                          startIcon={analyzingProposal ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                          {analyzingProposal
                            ? t("agentsListPage.skillParamsProposalAnalyzing")
                            : t("agentsListPage.skillParamsProposalAnalyze")}
                        </Button>
                      </Box>
                    </Stack>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t("agentsListPage.proposalStep3Title")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t("agentsListPage.proposalStep3Body")}
                  </Typography>
                  {Array.isArray(proposalCfg.fields_schema) && proposalCfg.fields_schema.length > 0 ? (
                    <List
                      dense
                      disablePadding
                      sx={{ bgcolor: "action.hover", borderRadius: 1, px: 1, py: 0.5 }}
                    >
                      {proposalCfg.fields_schema.map((f, idx) => (
                        <ListItem key={f.key} disablePadding sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={`${idx + 1}. ${f.label || f.key}`}
                            secondary={pdfSkillFieldSecondary(f, t)}
                            primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: "caption" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("agentsListPage.skillParamsProposalFieldsEmpty")}
                    </Typography>
                  )}

                  <PdfTemplateQualityPanel cfg={proposalCfg} t={t} />

                  {Array.isArray(proposalCfg.fields_schema) && proposalCfg.fields_schema.length > 0 && (
                    <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {t("agentsListPage.pdfTemplateAdjustTitle")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t("agentsListPage.pdfTemplateAdjustBody")}
                      </Typography>
                      {Array.isArray(proposalCfg.refinement_history) && proposalCfg.refinement_history.length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          {t("agentsListPage.pdfTemplateRefineHistory", {
                            count: proposalCfg.refinement_history.length,
                          })}
                        </Typography>
                      )}
                      <TextField
                        value={proposalFieldFeedback}
                        onChange={(e) => setProposalFieldFeedback(e.target.value)}
                        placeholder={t("agentsListPage.pdfTemplateAdjustPlaceholder")}
                        multiline
                        minRows={3}
                        maxRows={8}
                        fullWidth
                        size="small"
                        disabled={readOnly || refiningProposalFields}
                        inputProps={{ maxLength: 2000 }}
                        helperText={t("agentsListPage.pdfTemplateAdjustHelper")}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mt: 1.5 }}
                        onClick={handleRefineProposalFields}
                        disabled={readOnly || refiningProposalFields || !isEdit || !proposalFieldFeedback.trim()}
                        startIcon={refiningProposalFields ? <CircularProgress size={16} /> : null}
                      >
                        {refiningProposalFields
                          ? t("agentsListPage.pdfTemplateAdjustSubmitting")
                          : t("agentsListPage.pdfTemplateAdjustSubmit")}
                      </Button>
                      {proposalRefineResult && (
                        <Alert severity="success" sx={{ mt: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {t("agentsListPage.pdfTemplateRefineSuccess")}
                          </Typography>
                          {proposalRefineResult.detailLines.map((line) => (
                            <Typography key={line} variant="body2" sx={{ mt: 0.5 }}>
                              {line}
                            </Typography>
                          ))}
                        </Alert>
                      )}
                    </Box>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {t("agentsListPage.proposalStepOptionalTitle")}
                  </Typography>
                  <TextField
                    label={t("agentsListPage.skillParamsProposalOutputPattern")}
                    helperText={t("agentsListPage.skillParamsProposalOutputPatternHelper")}
                    value={proposalCfg.output_filename_pattern || "proposta-comercial.pdf"}
                    onChange={(e) =>
                      mergeSkillConfig("proposal_generator", { output_filename_pattern: e.target.value })
                    }
                    fullWidth
                    size="small"
                    disabled={readOnly}
                  />
                </Box>
              </Stack>
            </AgentFormSection>
          )}

          {/* Modelo de contrato (skill contract_generator) */}
          {engineType === "xpro" && primarySkillKey === "contract_generator" && (
            <AgentFormSection
              id="contract-pdf"
              title={t(meta("contract-pdf").labelKey)}
              hint={t(meta("contract-pdf").hintKey)}
              fullWidth
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                {t("agentsListPage.knowledgeSectionContractIntro")}
              </Typography>
              <Alert severity="info" sx={{ mb: 2.5, py: 0.75 }}>
                {t("agentsListPage.contractFormatNote")}
              </Alert>

              <Stack spacing={3} divider={<Divider flexItem />}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t("agentsListPage.contractStep1Title")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t("agentsListPage.contractStep1Body")}
                  </Typography>
                  <AgentDocumentsSection
                    documents={documents}
                    fileInputRef={fileInputRef}
                    handleClickAdd={handleClickAddDocs}
                    handleDrop={handleDropDocs}
                    handleDragOver={handleDragOverDocs}
                    moveDocument={moveDocument}
                    removeDocument={removeDocument}
                    readOnly={readOnly}
                    accept=".pdf,application/pdf"
                    embedded
                    embeddedDocTypeLabel={t("agentsListPage.contractUploadLabel")}
                    uploadTip={t("agentsListPage.contractUploadTip")}
                  />
                  <Alert severity="info" sx={{ mt: 1.5, py: 0.75 }}>
                    {t("agentsListPage.contractPlaceholderTip")}
                  </Alert>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t("agentsListPage.contractStep2Title")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t("agentsListPage.contractStep2Body")}
                  </Typography>
                  {pdfDocuments.length === 0 ? (
                    <Alert severity="info" sx={{ py: 0.75 }}>
                      {t("agentsListPage.contractStep2NeedPdf")}
                    </Alert>
                  ) : (
                    <Stack spacing={1.5}>
                      {pdfDocuments.length > 1 && (
                        <FormControl fullWidth size="small" disabled={readOnly}>
                          <InputLabel id="agent-contract-template-label">
                            {t("agentsListPage.skillParamsContractTemplateDoc")}
                          </InputLabel>
                          <Select
                            labelId="agent-contract-template-label"
                            label={t("agentsListPage.skillParamsContractTemplateDoc")}
                            value={contractCfg.template_document_id || pdfDocuments[0]?.id || ""}
                            onChange={(e) =>
                              mergeSkillConfig("contract_generator", {
                                template_document_id: e.target.value || "",
                              })
                            }
                          >
                            {pdfDocuments.map((d) => (
                              <MenuItem key={d.id || d.name} value={d.id || d.name}>
                                {d.name || d.file_name || "modelo.pdf"}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                      {!isEdit && (
                        <Alert severity="info" sx={{ py: 0.75 }}>
                          {t("agentsListPage.skillParamsContractAnalyzeNeedSave")}
                        </Alert>
                      )}
                      <Box>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleAnalyzeContractTemplate}
                          disabled={readOnly || analyzingContract || !isEdit}
                          startIcon={analyzingContract ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                          {analyzingContract
                            ? t("agentsListPage.skillParamsContractAnalyzing")
                            : t("agentsListPage.skillParamsContractAnalyze")}
                        </Button>
                      </Box>
                    </Stack>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t("agentsListPage.contractStep3Title")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t("agentsListPage.contractStep3Body")}
                  </Typography>
                  {Array.isArray(contractCfg.fields_schema) && contractCfg.fields_schema.length > 0 ? (
                    <List
                      dense
                      disablePadding
                      sx={{ bgcolor: "action.hover", borderRadius: 1, px: 1, py: 0.5 }}
                    >
                      {contractCfg.fields_schema.map((f, idx) => (
                        <ListItem key={f.key} disablePadding sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={`${idx + 1}. ${f.label || f.key}`}
                            secondary={pdfSkillFieldSecondary(f, t)}
                            primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: "caption" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("agentsListPage.skillParamsContractFieldsEmpty")}
                    </Typography>
                  )}

                  <PdfTemplateQualityPanel cfg={contractCfg} t={t} />

                  {Array.isArray(contractCfg.fields_schema) && contractCfg.fields_schema.length > 0 && (
                    <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {t("agentsListPage.pdfTemplateAdjustTitle")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t("agentsListPage.pdfTemplateAdjustBody")}
                      </Typography>
                      {Array.isArray(contractCfg.refinement_history) && contractCfg.refinement_history.length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          {t("agentsListPage.pdfTemplateRefineHistory", {
                            count: contractCfg.refinement_history.length,
                          })}
                        </Typography>
                      )}
                      <TextField
                        value={contractFieldFeedback}
                        onChange={(e) => setContractFieldFeedback(e.target.value)}
                        placeholder={t("agentsListPage.pdfTemplateAdjustPlaceholder")}
                        multiline
                        minRows={3}
                        maxRows={8}
                        fullWidth
                        size="small"
                        disabled={readOnly || refiningContractFields}
                        inputProps={{ maxLength: 2000 }}
                        helperText={t("agentsListPage.pdfTemplateAdjustHelper")}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mt: 1.5 }}
                        onClick={handleRefineContractFields}
                        disabled={readOnly || refiningContractFields || !isEdit || !contractFieldFeedback.trim()}
                        startIcon={refiningContractFields ? <CircularProgress size={16} /> : null}
                      >
                        {refiningContractFields
                          ? t("agentsListPage.pdfTemplateAdjustSubmitting")
                          : t("agentsListPage.pdfTemplateAdjustSubmit")}
                      </Button>
                      {contractRefineResult && (
                        <Alert severity="success" sx={{ mt: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {t("agentsListPage.pdfTemplateRefineSuccess")}
                          </Typography>
                          {contractRefineResult.detailLines.map((line) => (
                            <Typography key={line} variant="body2" sx={{ mt: 0.5 }}>
                              {line}
                            </Typography>
                          ))}
                        </Alert>
                      )}
                    </Box>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {t("agentsListPage.contractStepOptionalTitle")}
                  </Typography>
                  <TextField
                    label={t("agentsListPage.skillParamsContractOutputPattern")}
                    helperText={t("agentsListPage.skillParamsContractOutputPatternHelper")}
                    value={contractCfg.output_filename_pattern || "contrato.pdf"}
                    onChange={(e) =>
                      mergeSkillConfig("contract_generator", { output_filename_pattern: e.target.value })
                    }
                    fullWidth
                    size="small"
                    disabled={readOnly}
                  />
                </Box>
              </Stack>
            </AgentFormSection>
          )}

          {(engineType === "n8n" ||
            (engineType === "xpro" &&
              (primarySkillKey === "faq" ||
                primarySkillKey === "ticket_seller" ||
                primarySkillKey === "social_media"))) && (
            <AgentFormSection
              id="knowledge"
              title={t(meta("knowledge").labelKey)}
              hint={
                engineType === "n8n"
                  ? t("agentsListPage.formSectionKnowledgeHint")
                  : skillKnowledgeSectionHint
              }
              fullWidth
            >
              <AgentKnowledgePanel
                engineType={engineType}
                primarySkillKey={primarySkillKey}
                documents={documents}
                fileInputRef={fileInputRef}
                handleClickAddDocs={handleClickAddDocs}
                handleDropDocs={handleDropDocs}
                handleDragOverDocs={handleDragOverDocs}
                moveDocument={moveDocument}
                removeDocument={removeDocument}
                readOnly={readOnly}
                acceptDocs={ACCEPT_DOCS}
                sourceUrls={sourceUrls}
                newUrl={newUrl}
                setNewUrl={setNewUrl}
                handleAddUrl={handleAddUrl}
                handleRemoveUrl={handleRemoveUrl}
                knowledgePreviews={knowledgePreviews}
                onKnowledgePreviewUpdate={handleKnowledgePreviewUpdate}
              />
            </AgentFormSection>
          )}

          {engineType === "xpro" && (
            <AgentFormSection
              id="ai-settings"
              title={t(meta("ai-settings").labelKey)}
              hint={skillAiSettingsHint}
            >
              <AgentAiSettingsPanel
                model={model}
                setModel={setModel}
                temperature={temperature}
                setTemperature={setTemperature}
                systemPrompt={systemPrompt}
                setSystemPrompt={setSystemPrompt}
                readOnly={readOnly}
                skillKey={primarySkillKey}
                skillConfig={
                  skills[0]?.config && typeof skills[0].config === "object" && !Array.isArray(skills[0].config)
                    ? skills[0].config
                    : {}
                }
                agentName={name}
                sourceUrls={sourceUrls}
                documents={documents}
                knowledgePreviews={knowledgePreviews}
              />
            </AgentFormSection>
          )}

          {/* Ações */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              flexWrap: "wrap",
              pt: 2.5,
              mt: 0.5,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Button type="button" variant="outlined" onClick={() => navigate("/agents")} disabled={isSaving}>
              {isEdit ? "Voltar" : "Cancelar"}
            </Button>
            {(isEdit ? canUpdate : canCreate) && (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSaving || !name.trim() || (uploadProgress.total > 0 && uploadProgress.done < uploadProgress.total)}
              >
                {uploadProgress.total > 0 ? (
                  `Enviando ${uploadProgress.done}/${uploadProgress.total}...`
                ) : isSaving ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Salvando...
                  </>
                ) : isEdit ? (
                  "Salvar alterações"
                ) : (
                  "Criar agente de IA"
                )}
              </Button>
            )}
          </Box>
          </Stack>
        </form>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />

      {isEdit && id && (
        <AgentSimulator
          agentId={id}
          agentName={name}
          primarySkillKey={primarySkillKey}
          agentActive={status}
        />
      )}
    </Box>
  );
};

export default AgentForm;
